import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Check, Loader2, ShieldCheck, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const USER_TYPES = [
  { value: "schilder",              label: "Schilder" },
  { value: "decorateur",            label: "Decorateur" },
  { value: "afwerkingsbedrijf",     label: "Afwerkingsbedrijf" },
  { value: "schrijnwerk_renovatie", label: "Schrijnwerk / renovatie" },
  { value: "andere",                label: "Andere" },
];

/**
 * F19 — Publieke beta-aanmeldpagina (/beta-aanmelden).
 * Sober, professioneel, mobile-first. Geen auth vereist.
 */
export default function BetaSignup() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "", company: "", email: "", phone: "", country: "België",
    user_type: "", website: "", note: "",
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [counter, setCounter] = useState(null); // {used, total}

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/beta-waitlist/count`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d && setCounter(d))
      .catch(() => {});
  }, []);

  const update = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = "Verplicht";
    if (!form.company.trim()) errs.company = "Verplicht";
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = "Vul een geldig e-mailadres in";
    if (!form.phone.trim()) errs.phone = "Verplicht";
    if (!form.country.trim()) errs.country = "Verplicht";
    if (!form.user_type) errs.user_type = "Kies één optie";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/beta-waitlist`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (resp.status === 409) {
        const d = await resp.json().catch(() => ({}));
        toast.error(d?.detail || "Er is al een aanmelding met dit e-mailadres.");
      } else if (!resp.ok) {
        const d = await resp.json().catch(() => ({}));
        toast.error(d?.detail || "Aanmelden mislukt. Probeer het opnieuw.");
      } else {
        setSubmitted(true);
      }
    } catch {
      toast.error("Netwerkfout. Probeer het opnieuw.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#FAF7F1] flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-lg bg-white rounded-2xl border border-[#E5E2DC] shadow-sm p-6 sm:p-10 text-center"
             data-testid="beta-signup-confirmation">
          <div className="w-14 h-14 mx-auto rounded-full bg-[#EAF4EA] flex items-center justify-center mb-4">
            <Check className="w-7 h-7 text-[#2E5931]" />
          </div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#2C2C2A] mb-3">
            Bedankt voor je aanmelding.
          </h1>
          <p className="text-[#4F4F4D] leading-relaxed">
            We bekijken je aanvraag en nemen contact met je op zodra er een beta-plek beschikbaar is.
          </p>
          <div className="mt-8 pt-6 border-t border-[#E5E2DC] text-[10px] uppercase tracking-[0.2em] text-[#7A7A78]">
            Powered by EmmaForge™
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF7F1]" data-testid="beta-signup-page">
      <div className="max-w-2xl mx-auto px-4 py-10 sm:py-14">
        {/* Header */}
        <div className="mb-8 sm:mb-10">
          <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-[#5D7259] mb-3">
            EmmaMeasure™
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-[#2C2C2A] leading-tight" data-testid="beta-signup-title">
            EmmaMeasure™ gesloten beta
          </h1>
          <p className="text-base sm:text-lg text-[#4F4F4D] mt-4 leading-relaxed">
            Meet sneller op, maak offertes professioneler en test mee aan de slimme meetapp voor schilders en afwerkers.
          </p>
          <ul className="mt-6 space-y-1.5 text-sm text-[#4F4F4D]">
            <li className="flex items-start gap-2"><span className="text-[#5D7259] mt-0.5">•</span>Voor schilders, decorateurs en afwerkingsbedrijven.</li>
            <li className="flex items-start gap-2"><span className="text-[#5D7259] mt-0.5">•</span>Meet ruimtes, trappen, binnen- en buitenschrijnwerk.</li>
            <li className="flex items-start gap-2"><span className="text-[#5D7259] mt-0.5">•</span>Maak automatisch offertes en PDF&apos;s.</li>
            <li className="flex items-start gap-2"><span className="text-[#5D7259] mt-0.5">•</span>Test als eerste EmmaMeasure™.</li>
          </ul>
        </div>

        {/* Teller */}
        {counter && (
          <div className="mb-6 flex items-center gap-2 text-xs sm:text-sm text-[#5D7259]" data-testid="beta-counter">
            <ShieldCheck className="w-4 h-4 flex-shrink-0" />
            <span>
              <span className="font-semibold text-[#2C2C2A]">{Math.min(counter.used, counter.total)}</span>
              {" van "}
              <span className="font-semibold text-[#2C2C2A]">{counter.total}</span>
              {" beta-plekken gereserveerd"}
            </span>
          </div>
        )}

        {/* Formulier */}
        <form onSubmit={onSubmit} className="bg-white rounded-2xl border border-[#E5E2DC] shadow-sm p-5 sm:p-8 space-y-5" data-testid="beta-signup-form">
          <FieldRow label="Naam" required error={errors.name}>
            <Input value={form.name} onChange={(e) => update("name", e.target.value)}
                   className="h-11 rounded-xl" data-testid="beta-name" autoComplete="name" />
          </FieldRow>

          <FieldRow label="Bedrijfsnaam" required error={errors.company}>
            <Input value={form.company} onChange={(e) => update("company", e.target.value)}
                   className="h-11 rounded-xl" data-testid="beta-company" autoComplete="organization" />
          </FieldRow>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <FieldRow label="E-mailadres" required error={errors.email}>
              <Input type="email" value={form.email} onChange={(e) => update("email", e.target.value)}
                     className="h-11 rounded-xl" data-testid="beta-email" autoComplete="email" />
            </FieldRow>
            <FieldRow label="Telefoonnummer" required error={errors.phone}>
              <Input type="tel" value={form.phone} onChange={(e) => update("phone", e.target.value)}
                     className="h-11 rounded-xl" data-testid="beta-phone" autoComplete="tel" />
            </FieldRow>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <FieldRow label="Land" required error={errors.country}>
              <Input value={form.country} onChange={(e) => update("country", e.target.value)}
                     className="h-11 rounded-xl" data-testid="beta-country" autoComplete="country-name" />
            </FieldRow>
            <FieldRow label="Type gebruiker" required error={errors.user_type}>
              <Select value={form.user_type} onValueChange={(v) => update("user_type", v)}>
                <SelectTrigger className="h-11 rounded-xl" data-testid="beta-user-type">
                  <SelectValue placeholder="Kies..." />
                </SelectTrigger>
                <SelectContent>
                  {USER_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value} data-testid={`beta-user-type-${t.value}`}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FieldRow>
          </div>

          <FieldRow label="Website / Instagram" optional>
            <Input value={form.website} onChange={(e) => update("website", e.target.value)}
                   className="h-11 rounded-xl" data-testid="beta-website" placeholder="bv. www.jouwbedrijf.be of @jouwaccount" />
          </FieldRow>

          <FieldRow label="Waarvoor wil je EmmaMeasure vooral gebruiken?" optional>
            <Textarea value={form.note} onChange={(e) => update("note", e.target.value)}
                      className="min-h-[88px] rounded-xl" maxLength={1000} data-testid="beta-note"
                      placeholder="Korte opmerking (optioneel)" />
          </FieldRow>

          <Button type="submit" disabled={submitting}
                  className="w-full h-12 rounded-xl bg-[#D96C4A] hover:bg-[#C05A3C] text-white font-semibold disabled:opacity-50"
                  data-testid="beta-submit">
            {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Bezig met verzenden…</> : "Aanmelden voor beta"}
          </Button>

          <p className="text-[11px] text-[#7A7A78] text-center leading-relaxed">
            Door je aan te melden ga je akkoord dat we je gegevens gebruiken om contact op te nemen over de beta.
          </p>
        </form>

        <div className="mt-8 flex items-center justify-between text-[10px] uppercase tracking-[0.2em] text-[#7A7A78]">
          <button type="button" onClick={() => navigate("/login")} className="hover:text-[#5D7259] transition" data-testid="beta-login-link">
            Al toegang? Inloggen
          </button>
          <span>Powered by EmmaForge™</span>
        </div>
      </div>
    </div>
  );
}

function FieldRow({ label, required, optional, error, children }) {
  return (
    <div>
      <Label className="text-xs font-semibold text-[#2C2C2A] flex items-center gap-1.5 mb-1.5">
        {label}
        {required && <span className="text-[#D96C4A]">*</span>}
        {optional && <span className="text-[10px] font-normal text-[#7A7A78] uppercase tracking-wider">optioneel</span>}
      </Label>
      {children}
      {error && (
        <div className="mt-1 text-[11px] text-[#B4382A] flex items-center gap-1" role="alert">
          <AlertCircle className="w-3 h-3" /> {error}
        </div>
      )}
    </div>
  );
}
